import React, { useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Upload, FileSpreadsheet, Loader2, AlertCircle, 
  CheckCircle2, FileUp, X
} from 'lucide-react';

export function ImportUpload({ onUploadComplete }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Opções de upload
  const [options, setOptions] = useState({
    sheetName: 'ContasReceber',
    headerRow: 7,
    simulationOnly: true,
    ignoreDuplicates: false,
    allowCreateGuardian: false,
    skipAmbiguous: true
  });

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (selectedFile) => {
    const validExtensions = ['.xls', '.xlsx', '.csv'];
    const fileExtension = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Formato não suportado. Use .xls, .xlsx ou .csv');
      return;
    }
    
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Selecione um arquivo');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sheet_name', options.sheetName);
      formData.append('header_row', options.headerRow);
      formData.append('simulation_only', options.simulationOnly);
      formData.append('ignore_duplicates', options.ignoreDuplicates);
      formData.append('allow_create_guardian', options.allowCreateGuardian);
      formData.append('skip_ambiguous', options.skipAmbiguous);

      const res = await api.post('/finance/import-titles/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const rep = res.data?.report || {};
      const errBoxLines = [];
      if (rep.total_linhas_lidas !== undefined) errBoxLines.push(`Total lidas: ${rep.total_linhas_lidas}`);
      if (rep.linhas_importadas !== undefined) errBoxLines.push(`Importadas: ${rep.linhas_importadas}`);
      if (rep.linhas_com_erro) errBoxLines.push(`Com erro: ${rep.linhas_com_erro}`);
      if (rep.linhas_ignoradas_vazias) errBoxLines.push(`Ignoradas (vazias): ${rep.linhas_ignoradas_vazias}`);
      if (rep.linhas_ignoradas_totais) errBoxLines.push(`Ignoradas (totais): ${rep.linhas_ignoradas_totais}`);
      if (rep.primeira_linha_processada !== undefined && rep.ultima_linha_processada !== undefined) {
        errBoxLines.push(`Linhas: ${rep.primeira_linha_processada} → ${rep.ultima_linha_processada}`);
      }
      toast.success(
        `Arquivo processado! ${res.data.summary.total_rows} linhas encontradas.`,
        errBoxLines.length ? { description: errBoxLines.join(' · '), duration: 14000 } : undefined
      );
      if (rep.linhas_com_erro) {
        console.warn('[import] erros por linha:', rep.erros_por_linha);
      }
      onUploadComplete(res.data);
    } catch (error) {
      console.error('Erro no upload:', error);
      const detail = error.response?.data?.detail;
      // Backend pode mandar string ou objeto estruturado
      if (detail && typeof detail === 'object') {
        const lines = [detail.message || 'Erro ao processar arquivo'];
        if (detail.row_number) lines.push(`Linha com problema: ${detail.row_number}`);
        if (detail.rows_processed_ok !== undefined && detail.total_rows_detected !== undefined) {
          lines.push(`Progresso: ${detail.rows_processed_ok}/${detail.total_rows_detected} linhas processadas`);
        }
        if (detail.sheet_name_received) lines.push(`Aba enviada: "${detail.sheet_name_received}"`);
        if (detail.available_sheets) lines.push(`Abas disponíveis: ${detail.available_sheets.join(', ')}`);
        if (detail.header_row_received !== undefined) lines.push(`Linha de cabeçalho enviada: ${detail.header_row_received}`);
        if (detail.suggested_header_row !== undefined) lines.push(`💡 Sugestão: header_row=${detail.suggested_header_row}`);
        if (detail.columns_detected) lines.push(`Colunas detectadas: ${detail.columns_detected.slice(0, 8).join(', ')}${detail.columns_detected.length > 8 ? '…' : ''}`);
        toast.error(lines[0], { description: lines.slice(1).join('\n'), duration: 14000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erro ao processar arquivo');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5 text-purple-500" />
            Upload de Planilha
          </CardTitle>
          <CardDescription>
            Arraste ou selecione uma planilha .xls, .xlsx ou .csv
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`
              relative border-2 border-dashed rounded-xl p-8 text-center transition-all
              ${dragActive ? 'border-purple-500 bg-purple-50' : 'border-slate-300 hover:border-purple-400'}
              ${file ? 'bg-green-50 border-green-400' : ''}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              data-testid="file-input"
            />
            
            {file ? (
              <div className="space-y-3">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-500" />
                <div>
                  <p className="font-medium text-green-700">{file.name}</p>
                  <p className="text-sm text-green-600">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  <X className="w-4 h-4 mr-1" />
                  Remover
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400" />
                <div>
                  <p className="font-medium">Arraste o arquivo aqui</p>
                  <p className="text-sm text-muted-foreground">
                    ou clique para selecionar
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Formatos aceitos: .xls, .xlsx, .csv
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Options */}
      <Card>
        <CardHeader>
          <CardTitle>Opções de Importação</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sheetName">Nome da Aba</Label>
              <Input
                id="sheetName"
                value={options.sheetName}
                onChange={(e) => setOptions({ ...options, sheetName: e.target.value })}
                placeholder="ContasReceber"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="headerRow">Linha do Cabeçalho</Label>
              <Input
                id="headerRow"
                type="number"
                value={options.headerRow}
                onChange={(e) => setOptions({ ...options, headerRow: parseInt(e.target.value) || 0 })}
                min={0}
              />
              <p className="text-xs text-muted-foreground">Índice base 0 (linha 8 = índice 7)</p>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Somente Simular</Label>
                <p className="text-sm text-muted-foreground">
                  Processa o arquivo sem importar os títulos
                </p>
              </div>
              <Switch
                checked={options.simulationOnly}
                onCheckedChange={(checked) => setOptions({ ...options, simulationOnly: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Ignorar Duplicados</Label>
                <p className="text-sm text-muted-foreground">
                  Importa mesmo se houver títulos similares
                </p>
              </div>
              <Switch
                checked={options.ignoreDuplicates}
                onCheckedChange={(checked) => setOptions({ ...options, ignoreDuplicates: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Criar Responsável se Não Existir</Label>
                <p className="text-sm text-muted-foreground">
                  Cria novo responsável quando não encontrado pelo CPF
                </p>
              </div>
              <Switch
                checked={options.allowCreateGuardian}
                onCheckedChange={(checked) => setOptions({ ...options, allowCreateGuardian: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Pular Linhas Ambíguas</Label>
                <p className="text-sm text-muted-foreground">
                  Não importa linhas com múltiplos matches
                </p>
              </div>
              <Switch
                checked={options.skipAmbiguous}
                onCheckedChange={(checked) => setOptions({ ...options, skipAmbiguous: checked })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Button */}
      <div className="flex justify-end gap-3">
        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="bg-gradient-to-r from-purple-600 to-green-500 hover:from-purple-700 hover:to-green-600"
          data-testid="upload-button"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processando...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              {options.simulationOnly ? 'Simular Importação' : 'Importar Títulos'}
            </>
          )}
        </Button>
      </div>

      {/* Help */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Dicas para importação bem-sucedida:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Certifique-se que o CPF do responsável está preenchido corretamente</li>
                <li>O nome do aluno deve estar exatamente como cadastrado no sistema</li>
                <li>Valores monetários devem estar no formato brasileiro (999,64)</li>
                <li>Datas devem estar no formato DD/MM/AAAA</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
